import { Node } from "@xyflow/react";
import { atom } from "jotai";
import { loadable } from "jotai/utils";

import { DefaultNodeData, NodeData, Service } from "../types";
import { zapMetadataAtomFamily_SUSPENDS } from "../zaps";
import {
  flattenOneLevel,
  getUniqueAppsFromSteps,
  isZapErrorObject,
} from "../utils";

import { selectedNodesAtom } from "./selectedNodesAtom";
import { zapNodesAtom } from "./zapNodesAtom";

const uniqueSelectedZapIdsAtom = atom((get) => {
  const zapNodes = get(zapNodesAtom);
  const selectedZapNodes = zapNodes.filter((node) => node.selected);

  const uniqueSelectedZapIds = new Set(
    selectedZapNodes
      .filter((node) => Boolean(node.data.zapId)) // zapId is null when a Zap node is first created but no Zap has been selected yet
      .map((node) => node.data.zapId),
  );
  return Array.from(uniqueSelectedZapIds);
});

uniqueSelectedZapIdsAtom.debugLabel = "uniqueSelectedZapIdsAtom";

/**
 * Returns an array of unique app IDs from selected nodes. Handles both default and Zap nodes.
 * For Zap nodes, it recursively retrieves all app IDs from path steps.
 */
const uniqueAppsForSelectedNodesAtom_SUSPENDS = atom<Promise<string[]>>(
  async (get) => {
    const selectedNodes: Node<NodeData>[] = get(selectedNodesAtom);
    const defaultNodes = selectedNodes.filter(
      (node) => node.type === "default",
    );
    const uniqueZapIds = get(uniqueSelectedZapIdsAtom);

    const zapNodeApps = await Promise.all(
      uniqueZapIds.map(async (zapId) => {
        const zap = await get(zapMetadataAtomFamily_SUSPENDS(zapId));
        if (!zap || isZapErrorObject(zap)) {
          return [];
        }

        const steps = zap.steps || [];
        return getUniqueAppsFromSteps(steps);
      }),
    );

    const zapNodeAppsFlattened = flattenOneLevel(zapNodeApps);

    const defaultNodeApps = defaultNodes
      .reduce(
        (acc, curr) => [
          ...acc,
          ...((curr.data as DefaultNodeData).services || []),
        ],
        [] as Service[],
      )
      .filter(Boolean);

    const allApps: string[] = [
      ...defaultNodeApps.map((app) => app.currentImplementation?.id ?? ""),
      ...zapNodeAppsFlattened,
    ];

    const uniqueAppsSet = new Set([...allApps]);

    return Array.from(uniqueAppsSet);
  },
);

uniqueAppsForSelectedNodesAtom_SUSPENDS.debugLabel =
  "uniqueAppsForSelectedNodesAtom_SUSPENDS";

// Does NOT suspend due to loadable usage
export const loadableUniqueAppsForSelectedNodesAtom = loadable(
  uniqueAppsForSelectedNodesAtom_SUSPENDS,
);
loadableUniqueAppsForSelectedNodesAtom.debugLabel =
  "loadableUniqueAppsForSelectedNodesAtom";
